import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { CookieService } from "ngx-cookie-service";
import { map, Observable } from "rxjs";
import { environment } from "src/environments/environment";
import { EUserFunctionality, EUserVisibility, IUser, TUserFunctionality, TUserVisibility } from "../modules/shared/models/user";
import { LoggerService } from "./logger.service";
import { EPolicyTypes, IBIPS_PRODUCTS, NO_IBIPS_PRODUCTS } from "../modules/shared/models/policy.model";

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {

  private _tokenKey = 'COOKIE_JWT';
  private _jwt = null;
  private _loggedUser: IUser = null;

  constructor(
    private readonly _http: HttpClient,
    private readonly _cookieService: CookieService
  ) { }

  get jwt(): string {
    return this._jwt;
  }

  get hasLoggedIn(): boolean {
    return !!this._jwt && !!this._loggedUser;
  }

  get loggedUser(): IUser {
    return this._loggedUser;
  }

  private getFunctionalityMode(key: string, index: number): string {
    if (this._loggedUser && this._loggedUser.profilo && this._loggedUser.profilo.length > 0) {
      const elem = this._loggedUser.profilo.find(x => x.startsWith(key));
      if (elem) {
        const mode = elem.split('.')[index];
        if (mode) {
          return mode;
        }
      }
    }
    return null;
  }

  get ibipMode(): TUserFunctionality {
    return this.getFunctionalityMode('FA_FaSuite.Prodotti.Ibip', 3) as EUserFunctionality;
  }

  get noIbipMode(): TUserFunctionality {
    return this.getFunctionalityMode('FA_FaSuite.Prodotti.NoIbip', 3) as EUserFunctionality;
  }

  get prevPipMode(): TUserFunctionality {
    return this.getFunctionalityMode('FA_FaSuite.Prodotti.PrevPip', 3) as EUserFunctionality;
  }

  get visibilityMode(): TUserVisibility {
    return this.getFunctionalityMode('FA_FaSuite.Visibilita', 2) as EUserVisibility;
  }

  get dashboardMode(): TUserVisibility {
    return this.getFunctionalityMode('FA_FaSuite.Cruscotto', 2) as EUserVisibility;
  }

  get reportMode(): TUserVisibility {
    return this.getFunctionalityMode('FA_FaSuite.Reportistica', 2) as EUserVisibility;
  }

  get canAccessUser(): boolean {
    // Se l'utente non è Cesare Ponti, può accedere
    if (!this.loggedUser.isCesarePonti) {
      return true;
    }
    // Se è Cesare Ponti su BCP
    if (this.loggedUser.isCesarePontiInBCP) {
      // Se è fuori sede, verifica se è abilitato
      if (this.loggedUser.isFuoriSede) {
        return this.loggedUser.isOperatoreAbilitatoOFS
      }
      // Se non è fuori sede, può accedere
      return true;
    }
    // Se è Cesare Ponti su Bper
    if (this.loggedUser.isCesarePontiInBper) {
      // Se è fuori sede, non può accedere
      return !this.loggedUser.isFuoriSede;
    }
    // Altrimenti non può accedere
    return false;
  }

  get hideKpis(): boolean {
    return ['DG', 'MDS', 'DT'].includes(this.dashboardMode);
  }

  canOperateUser(): boolean {
    // Se non è abilitato all'accesso non può operare
    if (!this.canAccessUser) {
      return false;
    }
    // Se è Cesare Ponti
    if (this.loggedUser.isCesarePontiInBCP) {
      // Cesare Ponti su Cesare Ponti in sede può operare
      if (!this.loggedUser.isFuoriSede) {
        return true;
      }
      // Cesare Ponti fuori sede deve essere operatore abilitato e OFS abilitato
      return this.loggedUser.isOperatoreAbilitatoOFS && this.loggedUser.isOFSAbilitato;
    }
    // Se è Cesare Ponti su BPER
    if (this.loggedUser.isCesarePontiInBper) {
      // Cesare Ponti su BPER può operare se è in sede e accreditato
      return !this.loggedUser.isFuoriSede && this.loggedUser.isAccreditato;
    }
    // Default: non può operare
    return false;
  }

  getJwtFromCookie(): string {
    const jwt = this._cookieService.get(this._tokenKey);
    this._jwt = jwt;
    LoggerService.logInfo(this.constructor.name, 'getJwtFromCookie', { jwt });
    return jwt;
  }

  deleteJwtFromCookie() {
    LoggerService.logInfo(this.constructor.name, 'deleteJwtFromCookie');
    this._jwt = null;
    this._loggedUser = null;
    this._cookieService.delete(this._tokenKey);
  }

  getUserInfo(): Observable<IUser> {
    const url = environment.apiBaseUrl + environment.apiPaths.userInfo;
    return this._http.get<IUser>(url, { withCredentials: true })
      .pipe(
        map(x => {
          this._loggedUser = x;
          return x;
        }));
  }

  checkPendingOperations(): Observable<{check: boolean}> {
    const url = environment.apiBaseUrl + environment.apiPaths.pendingOperations;
    return this._http.get<{check: boolean}>(url);
  }

  hasWriteAbilitation(policyType: EPolicyTypes): boolean {
    if (IBIPS_PRODUCTS.includes(policyType)) {
      return this.ibipMode === EUserFunctionality.W;
    } else if (NO_IBIPS_PRODUCTS.includes(policyType)) {
      return this.noIbipMode === EUserFunctionality.W;
    } else {
      return this.prevPipMode === EUserFunctionality.W;
    }
  }

  hasNoWriteAbilitation(policyType: EPolicyTypes): boolean {
    if (IBIPS_PRODUCTS.includes(policyType)) {
      return this.ibipMode !== EUserFunctionality.W;
    } else if (NO_IBIPS_PRODUCTS.includes(policyType)) {
      return this.noIbipMode !== EUserFunctionality.W;
    } else {
      return this.prevPipMode !== EUserFunctionality.W;
    }
  }

  isHelpDeskAccount(policyType: EPolicyTypes): boolean {
    if (IBIPS_PRODUCTS.includes(policyType)) {
      return this.ibipMode === EUserFunctionality.HD;
    } else if (NO_IBIPS_PRODUCTS.includes(policyType)) {
      return this.noIbipMode === EUserFunctionality.HD;
    } else {
      return this.prevPipMode === EUserFunctionality.HD;
    }
  }

}
